import React, { useEffect, useState } from "react";
import { Plus, Edit2, X, ToggleLeft, ToggleRight, Users } from "lucide-react";
import Swal2 from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import "./app.css";
import Loading from "./loading";
import { secretKey } from "./data";

const MySwal = withReactContent(Swal2);
const API = "https://apiftpsplit.ftpeople.pl";
const hdrs = { "Content-Type": "application/json", Authorization: secretKey };

const TYPES = [
  { value: "CHF", label: "CHF" },
  { value: "SKD", label: "SKD" },
  { value: "WIBOR", label: "WIBOR" },
  { value: "SLUZEBNOSC", label: "SŁUŻEBNOŚĆ" },
];

const EMPTY_FORM = {
  name: "",
  mail: "",
  price: "",
  company: "CHF",
  active: "1",
  id: null,
};

const SendClientsConf = () => {
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [sendType, setSendType] = useState("CHF");
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // "add" | "edit"
  const [form, setForm] = useState({ ...EMPTY_FORM });

  /* ── fetch ── */
  const getData = async () => {
    try {
      const res = await fetch(`${API}/getSendClientsConf`, {
        method: "POST",
        headers: hdrs,
        body: "{}",
      });
      setClients(await res.json());
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    getData();
  }, []);

  /* ── form helpers ── */
  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const openAdd = () => {
    setForm({ ...EMPTY_FORM, company: sendType });
    setModalMode("add");
    setShowModal(true);
  };

  const openEdit = (client) => {
    setForm({
      name: client.name,
      mail: client.mail,
      price: client.price,
      company: client.company,
      active: String(client.active),
      id: client.id,
    });
    setModalMode("edit");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setForm({ ...EMPTY_FORM });
  };

  const submitForm = async () => {
    if (!form.name || !form.mail || !form.price || !form.company) {
      MySwal.fire({
        icon: "error",
        title: "Wypełnij",
        text: "wszystkie wymagane pola",
      });
      return;
    }

    const endpoint =
      modalMode === "add" ? "addSendClientsConf" : "editSendClientsConf";
    const body =
      modalMode === "add"
        ? {
            name: form.name,
            mail: form.mail,
            price: form.price,
            company: form.company,
            active: form.active,
          }
        : {
            name: form.name,
            mail: form.mail,
            price: form.price,
            company: form.company,
            active: form.active,
            id: form.id,
          };

    try {
      await fetch(`${API}/${endpoint}`, {
        method: "POST",
        headers: hdrs,
        body: JSON.stringify(body),
      });
      MySwal.fire({
        icon: "success",
        title: modalMode === "add" ? "Dodano" : "Edytowano",
        text: "klienta",
      });
      closeModal();
      getData();
    } catch (err) {
      console.error(err);
    }
  };

  /* ── bulk actions ── */
  const bulkToggle = async (activate) => {
    const endpoint = activate ? "activateAllType" : "deactivateAllType";
    await fetch(`${API}/${endpoint}`, {
      method: "POST",
      headers: hdrs,
      body: JSON.stringify({ type: sendType }),
    }).catch(() => {});
    getData();
  };

  const filtered = clients.filter((c) => c.company === sendType);

  if (loading) return <Loading />;

  return (
    <div className="scc-wrapper">
      {/* ── Toolbar ── */}
      <div className="scc-toolbar">
        <div className="scc-toolbar-left">
          <select
            className="db-input db-select"
            value={sendType}
            onChange={(e) => setSendType(e.target.value)}
          >
            {TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <span className="lq-count">{filtered.length} klientów</span>
        </div>
        <div className="scc-toolbar-right">
          <button className="lq-btn lq-btn--primary" onClick={openAdd}>
            <Plus size={14} /> Dodaj
          </button>
          <button
            className="lq-btn lq-btn--success"
            onClick={() => bulkToggle(true)}
          >
            <ToggleRight size={14} /> Aktywuj wszystkie
          </button>
          <button
            className="lq-btn lq-btn--danger"
            onClick={() => bulkToggle(false)}
          >
            <ToggleLeft size={14} /> Deaktywuj wszystkie
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="lq-table-wrap db-table-scroll">
        <table className="lq-table">
          <thead>
            <tr>
              <th>Nazwa</th>
              <th>E-mail</th>
              <th>Cena</th>
              <th>Dział</th>
              <th>Aktywny</th>
              <th>Akcja</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id}>
                <td>{c.name}</td>
                <td className="lq-td-mono">{c.mail}</td>
                <td>{c.price} PLN</td>
                <td>{c.company}</td>
                <td>
                  <span
                    className={`ld-status ${
                      +c.active === 1 ? "ld-status--done" : "ld-status--pending"
                    }`}
                  >
                    {+c.active === 1 ? "TAK" : "NIE"}
                  </span>
                </td>
                <td>
                  <button
                    className="tc-action-btn tc-action-btn--view"
                    onClick={() => openEdit(c)}
                  >
                    <Edit2 size={13} /> Edytuj
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="lq-empty-row">
                  Brak klientów dla tego działu
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Modal (add/edit) ── */}
      {showModal && (
        <div
          className="sn-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="sn-modal">
            <div className="sn-header">
              <div>
                <h2 className="sn-title">
                  {modalMode === "add" ? "Dodaj klienta" : "Edytuj klienta"}
                </h2>
                <span className="sn-subtitle">
                  {modalMode === "edit" ? form.name : "Nowy klient wysyłkowy"}
                </span>
              </div>
              <button className="ld-close" onClick={closeModal}>
                <X size={18} />
              </button>
            </div>

            <div className="sn-body">
              <div className="scc-form-grid">
                <div className="rp-field">
                  <label className="db-field-label">
                    Nazwa <span className="scc-req">*</span>
                  </label>
                  <input
                    type="text"
                    className="db-input"
                    value={form.name}
                    onChange={(e) => setField("name", e.target.value)}
                  />
                </div>
                <div className="rp-field">
                  <label className="db-field-label">
                    Adres e-mail <span className="scc-req">*</span>
                  </label>
                  <input
                    type="email"
                    className="db-input"
                    value={form.mail}
                    onChange={(e) => setField("mail", e.target.value)}
                  />
                </div>
                <div className="rp-field">
                  <label className="db-field-label">
                    Cena <span className="scc-req">*</span>
                  </label>
                  <input
                    type="number"
                    className="db-input"
                    value={form.price}
                    onChange={(e) => setField("price", e.target.value)}
                  />
                </div>
                <div className="rp-field">
                  <label className="db-field-label">Aktywna</label>
                  <select
                    className="db-input db-select"
                    value={form.active}
                    onChange={(e) => setField("active", e.target.value)}
                  >
                    <option value="1">TAK</option>
                    <option value="0">NIE</option>
                  </select>
                </div>
                <div className="rp-field">
                  <label className="db-field-label">
                    Rodzaj leadów <span className="scc-req">*</span>
                  </label>
                  <select
                    className="db-input db-select"
                    value={form.company}
                    onChange={(e) => setField("company", e.target.value)}
                  >
                    {TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="scc-form-footer">
                <button className="lq-btn lq-btn--muted" onClick={closeModal}>
                  Anuluj
                </button>
                <button className="lq-btn lq-btn--success" onClick={submitForm}>
                  {modalMode === "add" ? "Dodaj" : "Zapisz zmiany"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SendClientsConf;
